import { silaeService, userService } from '@libs/data-access/queries'
import { FieldSet } from '@libs/ui/ds'
import { Controller } from '@libs/ui/form'
import { usePlanningFormTranslation } from '@libs/utils/translations'
import type { NewUserFormType } from '@spa/components/FormNewUser/hooks/useNewUserForm'
import type { UserInfoFormData } from '@spa/scenes/MemberPage/modals/UserInfoModal/hooks/useUserInfoForm'
import type { FC } from 'react'
import React, { useEffect } from 'react'
import { useFormContext } from 'react-hook-form'

type Props = {
  disabledSilaeSync?: boolean
  locationIdForSilaeSync?: number
}

export type SilaeSyncFieldType = {
  payroll_provider_sync: boolean
}

export const SilaeSyncFieldId: FC<Props> = ({
  disabledSilaeSync = true,
  locationIdForSilaeSync
}) => {
  const { t } = usePlanningFormTranslation()

  const permissions = userService.membership.useFeaturePermissions()

  const silaeConfigurationQuery =
    silaeService.configuration.useFetchConfiguration({
      enabled: permissions?.hr_silae_hr_synchronisation?.can_read,
      initialData: { first_synchronization: false, ready_to_use: false }
    })

  const silaeLocationsQuery = silaeService.locations.useFetch({
    initialData: [],
    enabled: permissions?.hr_silae_hr_synchronisation?.can_read
  })

  const {
    watch,
    resetField,
    control,
    formState: { errors }
  } = useFormContext<UserInfoFormData | NewUserFormType>()

  useEffect(() => {
    if (
      !watch('employee_id')?.length &&
      permissions?.hr_silae_hr_synchronisation?.can_read
    ) {
      resetField('payroll_provider_sync', { defaultValue: false })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watch('employee_id')])

  if (
    !permissions?.hr_silae_hr_synchronisation?.can_read ||
    silaeLocationsQuery.isLoading
  ) {
    return null
  }

  const locationNotLinkedToSilae = locationIdForSilaeSync
    ? silaeLocationsQuery.data?.find(
        location => location.id === locationIdForSilaeSync
      )?.case_number === null
    : true

  return (
    <FieldSet className='mt-2'>
      <Controller.Switch
        className='items-center'
        errorMessage={errors?.payroll_provider_sync?.message}
        fillWidth
        label={t('membershipCreation.syncSilae.label')}
        desc={t('membershipCreation.syncSilae.desc')}
        name='payroll_provider_sync'
        control={control}
        disabled={
          disabledSilaeSync ||
          silaeConfigurationQuery?.data?.ready_to_use === false ||
          locationNotLinkedToSilae
        }
      />
    </FieldSet>
  )
}
